import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import { Mutation, Query } from "react-apollo";

import { DELETE_AUDIO, GET_AUDIO_FILES, UPLOAD_AUDIO } from "../../gql";

import Pagination from "../Pagination";
import Throbber from "../Throbber";

import closeIcon from "../../../images/icons/close-white.svg";

const ACCEPTED_AUDIO_TYPES =
  ".mp3,audio/mpeg,audio/mp3,audio/mpeg3,audio/x-mpeg-3";

const stripExtension = (fileName) => fileName.replace(/\.[^.]+$/, "");

class Mp3AudioChooser extends PureComponent {
  constructor() {
    super();
    this.state = {
      fileIsValid: false,
      audio: null,
      displayName: "",
    };
    this.paginationLimit = 10;
  }

  deleteAudio = (id, deleteAudio) => {
    if (confirm("Are you sure you want to delete this audio file?")) {
      deleteAudio({
        variables: { id },
      });
    }
  };

  audioUploadHandle = ({
    target: {
      validity,
      files: [file],
    },
  }) => {
    const { displayName } = this.state;

    this.setState({
      fileIsValid: validity.valid,
      audio: validity.valid ? file : null,
      displayName:
        displayName || (validity.valid ? stripExtension(file.name) : ""),
    });
  };

  displayNameHandle = ({ target: { value } }) => {
    this.setState({ displayName: value });
  };

  submitAudio = (event, uploadAudio) => {
    event.preventDefault();
    const { audio, displayName } = this.state;

    uploadAudio({
      variables: {
        audio,
        displayName: displayName.trim(),
      },
    });
  };

  render() {
    const { fileIsValid, displayName } = this.state;
    const { onClickAudio } = this.props;
    const uploadButtonDisabled = !fileIsValid || !displayName.trim();

    return (
      <Fragment>
        <Query
          query={GET_AUDIO_FILES}
          variables={{
            limit: this.paginationLimit,
            cursor: "1",
          }}
        >
          {({ data, loading, error, fetchMore }) => {
            if (loading)
              return (
                <p className="full-width center-align">
                  Loading audio files...
                </p>
              );
            if (error)
              return <p className="full-width center-align">{error.message}</p>;
            const { audioFiles } = data;

            return (
              <div className="overlay__image-wrapper">
                {audioFiles.edges.length > 0 ? (
                  audioFiles.edges.map((audio) => (
                    <div className="relative-block full-width" key={audio.id}>
                      <Mutation
                        mutation={DELETE_AUDIO}
                        refetchQueries={[
                          {
                            query: GET_AUDIO_FILES,
                            variables: {
                              limit: this.paginationLimit,
                              cursor: "1",
                            },
                          },
                        ]}
                      >
                        {(deleteAudio, { loading }) => [
                          <Throbber
                            key="throbber"
                            hidden={!loading}
                            loadingText="Deleting audio file..."
                          />,
                          <button
                            key="select-button"
                            className="button button--small"
                            title={audio.fileName}
                            onClick={() =>
                              onClickAudio(
                                audio.path,
                                audio.id,
                                audio.displayName,
                              )
                            }
                          >
                            {audio.displayName}
                          </button>,
                          <audio
                            key="preview"
                            controls
                            src={audio.path}
                            style={{
                              verticalAlign: "middle",
                            }}
                          />,
                          <button
                            key="delete-button"
                            title="Delete"
                            onClick={() =>
                              this.deleteAudio(audio.id, deleteAudio)
                            }
                            className="button button--round-icon button--delete"
                          >
                            <span className="sr-only">Delete</span>
                            <img
                              src={closeIcon}
                              alt="Delete"
                              className="button__icon-small"
                            />
                          </button>,
                        ]}
                      </Mutation>
                    </div>
                  ))
                ) : (
                  <p className="full-width center-align">
                    No audio files found.
                  </p>
                )}
                <div className="full-width">
                  <Pagination
                    disableLoadMore={!audioFiles.pageInfo.hasNextPage}
                    onLoadMore={() =>
                      fetchMore({
                        query: GET_AUDIO_FILES,
                        variables: {
                          limit: this.paginationLimit,
                          cursor: audioFiles.pageInfo.endCursor,
                        },
                        updateQuery: (previousResult, { fetchMoreResult }) => {
                          const previous = previousResult.audioFiles;
                          const newAudioFiles =
                            fetchMoreResult.audioFiles.edges;

                          const newCursor =
                            fetchMoreResult.audioFiles.pageInfo.endCursor;

                          const hasNextPage =
                            fetchMoreResult.audioFiles.pageInfo.hasNextPage;

                          return {
                            cursor: newCursor,
                            audioFiles: {
                              edges: [...previous.edges, ...newAudioFiles],
                              __typename: previous.__typename,
                              pageInfo: {
                                endCursor: newCursor,
                                hasNextPage,
                                __typename: previous.pageInfo.__typename,
                              },
                            },
                          };
                        },
                      })
                    }
                  />
                </div>
              </div>
            );
          }}
        </Query>
        <Mutation
          mutation={UPLOAD_AUDIO}
          refetchQueries={[
            {
              query: GET_AUDIO_FILES,
              variables: {
                limit: this.paginationLimit,
                cursor: "1",
              },
            },
          ]}
        >
          {(uploadAudio, { loading, error }) => (
            <form
              className="form center-align u-margin--top-medium"
              onSubmit={(event) => this.submitAudio(event, uploadAudio)}
            >
              <label>
                <input
                  name="audio"
                  type="file"
                  accept={ACCEPTED_AUDIO_TYPES}
                  onChange={this.audioUploadHandle}
                />
              </label>
              <label>
                <input
                  name="displayName"
                  type="text"
                  value={displayName}
                  placeholder="Display name"
                  style={{
                    display: "block",
                    margin: "8px auto 0 auto",
                  }}
                  onChange={this.displayNameHandle}
                />
              </label>
              <input
                type="submit"
                value="Upload mp3"
                disabled={uploadButtonDisabled || loading}
                className="form__submit button button--small"
              />
              {loading && (
                <div className="u-loader__rotate u-loader__rotate--blue">
                  <span className="sr-only">Uploading audio file...</span>
                </div>
              )}
              {error && (
                <p className="full-width center-align">{error.message}</p>
              )}
            </form>
          )}
        </Mutation>
        <p className="small-text center-align">
          NB! Only mp3 files can be uploaded. The display name is the given name
          of the file, displayed in the apps.
        </p>
      </Fragment>
    );
  }
}

Mp3AudioChooser.propTypes = {
  onClickAudio: PropTypes.func,
};

export default Mp3AudioChooser;
